/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Render from '../../../render/Render';
import {dlsColor, dlsLayout, dlsZIndex} from '../../../theme/theme';
import {MenuItem, MenuItems} from '../../../types/components';
import {BackButton} from '../../buttons/BackButton';
import {ForwardButton} from '../../buttons/ForwardButton';

/**
 * Defines the fixed secondary-navigation bar shown above the page content for
 * current-theme primary navigation items with large submenu sets.
 */
const CONTROL_BUTTON_SIZE = 44;

interface NavigationTopBarProps {
  activeLabel?: string;
  collapsed?: boolean;
  currentPage: string;
  mobile?: boolean;
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem, isMainMenu?: boolean): void;
  secondaryMenuItems: MenuItems;
  visible: boolean;
};

/**
 * Defines style inputs that vary by host navigation state.
 */
interface StyleProps {
  collapsed?: boolean;
  mobile?: boolean;
}

const useStyles = makeStyles(() => ({
  root : {
    alignItems      : 'flex-start',
    backgroundColor : dlsColor('navBarGrey'),
    borderBottom    : `2px solid ${dlsColor('pastelTeal')}`,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    height          : 88,
    justifyContent  : 'center',
    left            : ({collapsed, mobile}: StyleProps) => mobile
      ? 0
      : collapsed
        ? dlsLayout('collapsedSidebar').width
        : dlsLayout('sidebar').width,
    padding  : '0 24px',
    position : 'fixed',
    right    : 0,
    top      : ({mobile}: StyleProps) => mobile ? dlsLayout('header').mobile : 0,
    zIndex   : ({mobile}: StyleProps) => mobile
      ? (dlsZIndex('navigationDrawerPaper') - 1) as any
      : dlsZIndex('navigationDrawerPaper') as any
  },
  header : {
    alignItems    : 'center',
    display       : 'flex',
    height        : 44,
    textTransform : 'uppercase',
    width         : '100%'
  },
  headerText : {
    color      : dlsColor('grey1'),
    fontFamily : 'Roboto',
    fontSize   : 16,
    fontWeight : 600,
    lineHeight : 'normal',
    whiteSpace : 'nowrap',
    wordBreak  : 'break-word'
  },
  tabBar : {
    alignItems : 'flex-start',
    display    : 'flex',
    minWidth   : 0,
    width      : '100%'
  },
  tabGroup : {
    alignItems : 'center',
    display    : 'flex',
    flex       : 1,
    gap        : 16,
    minWidth   : 0,
    overflow   : 'hidden'
  },
  scrollArea : {
    alignItems             : 'center',
    display                : 'flex',
    flex                   : 1,
    gap                    : 16,
    height                 : 44,
    minWidth               : 0,
    overflowX              : 'auto',
    overflowY              : 'hidden',
    scrollBehavior         : 'smooth',
    scrollbarWidth         : 'none',
    '&::-webkit-scrollbar' : {
      display : 'none'
    }
  },
  tab : {
    alignItems         : 'flex-start',
    background         : 'transparent',
    border             : 0,
    borderRadius       : 8,
    boxSizing          : 'border-box',
    color              : dlsColor('grey1'),
    cursor             : 'pointer',
    display            : 'flex',
    flexDirection      : 'column',
    flexShrink         : 0,
    font               : 'inherit',
    gap                : 8,
    margin             : 0,
    padding            : '4px 0 0',
    position           : 'relative',
    '&:hover $tabPill' : {
      backgroundColor : dlsColor('grey6')
    },
    '&:hover $tabText' : {
      fontWeight : 700
    },
    '&:hover $tabIndicator' : {
      borderTopColor : dlsColor('pastelTeal')
    },
    '&:active $tabPill' : {
      backgroundColor : dlsColor('aquamarine')
    },
    '&:active $tabText' : {
      color      : dlsColor('darkTeal'),
      fontWeight : 700
    },
    '&:active $tabIndicator' : {
      borderTopColor : dlsColor('darkTeal')
    },
    '&:focus-visible' : {
      outline : 'none'
    },
    '&:focus-visible $tabPill' : {
      border : `2px solid ${dlsColor('skyBlue')}`
    },
    '&:focus-visible $tabIndicator' : {
      borderTopColor : dlsColor('pastelTeal')
    }
  },
  tabPill : {
    alignItems     : 'center',
    border         : '2px solid transparent',
    borderRadius   : 8,
    boxSizing      : 'border-box',
    display        : 'flex',
    gap            : 8,
    height         : 32,
    justifyContent : 'center',
    overflow       : 'visible',
    padding        : '4px 8px',
    width          : '100%'
  },
  tabPillActive : {
    backgroundColor : dlsColor('paleBlue')
  },
  tabTextGroup : {
    height        : 16,
    display       : 'flex',
    overflow      : 'hidden',
    flexDirection : 'column'
  },
  tabText : {
    color         : dlsColor('grey1'),
    fontFamily    : 'Roboto',
    fontSize      : 14,
    fontWeight    : 500,
    height        : 16,
    letterSpacing : '0.5px',
    lineHeight    : '16px',
    textAlign     : 'left',
    textTransform : 'capitalize',
    whiteSpace    : 'nowrap',
    wordBreak     : 'break-word'
  },
  tabTextActive : {
    color      : dlsColor('darkTeal'),
    fontWeight : 700
  },
  tabTextSizer : {
    opacity    : 0,
    fontWeight : 700
  },
  betaTag : {
    alignItems      : 'center',
    backgroundColor : dlsColor('betaYellow'),
    borderRadius    : 4,
    boxShadow       : '0 1px 2px rgba(0, 0, 0, 0.12)',
    boxSizing       : 'border-box',
    color           : dlsColor('grey1'),
    display         : 'inline-flex',
    flexShrink      : 0,
    fontFamily      : 'Inter',
    fontSize        : 10,
    fontWeight      : 600,
    height          : 24,
    letterSpacing   : '0.5px',
    lineHeight      : '16px',
    padding         : 4,
    textAlign       : 'center',
    textTransform   : 'uppercase'
  },
  tabIndicator : {
    borderTop : '2px solid transparent',
    height    : 0,
    width     : '100%'
  },
  tabIndicatorActive : {
    borderTopColor : dlsColor('darkTeal')
  },
  scrollButton : {
    flexShrink : 0
  }
}));

/**
 * Resolves whether a secondary menu item should be visually selected for the
 * current route. Child routes under the menu item's page remain selected.
 */
const isSelectedItem = (item: MenuItem, currentPage: string): boolean => (
  !!item?.page && (item.page === currentPage || currentPage?.startsWith(`${item.page}/`))
);

/**
 * Renders the current-theme fixed top navigation for large secondary menu sets.
 *
 * The component keeps the selected submenu tab in view, shows scroll controls
 * only when overflow exists, and mirrors side-nav metadata such as beta tags.
 */
const NavigationTopBar = ({
  activeLabel,
  collapsed,
  currentPage,
  mobile = false,
  onSetPage,
  secondaryMenuItems,
  visible
}: NavigationTopBarProps) => {
  const classes                             = useStyles({collapsed, mobile});
  const scrollRef                           = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const remaining = element.scrollWidth - element.scrollLeft - element.clientWidth;
    setCanScrollLeft(element.scrollLeft > CONTROL_BUTTON_SIZE);
    setCanScrollRight(remaining > CONTROL_BUTTON_SIZE);
  }, []);

  const scrollBy = useCallback((direction: -1 | 1) => {
    scrollRef.current?.scrollBy({
      left     : direction * 240,
      behavior : 'smooth'
    });
  }, []);

  const handleScrollLeft = useCallback(() => {
    scrollBy(-1);
  }, [scrollBy]);

  const handleScrollRight = useCallback(() => {
    scrollBy(1);
  }, [scrollBy]);

  const handleTabClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const index = Number(event.currentTarget.dataset.index);
    if (Number.isNaN(index)) {
      return;
    }
    const item = secondaryMenuItems[index];
    if (item?.page) {
      onSetPage(item.page, index, true, item, false);
    }
  }, [onSetPage, secondaryMenuItems]);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [secondaryMenuItems, updateScrollState]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const selected = scrollRef.current?.querySelector('[data-selected="true"]') as HTMLElement;
      selected?.scrollIntoView({
        behavior : 'smooth',
        block    : 'nearest',
        inline   : 'center'
      });
      updateScrollState();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentPage, secondaryMenuItems, updateScrollState, visible]);

  if (!visible) {
    return null;
  }

  return (
    <nav className={classes.root} aria-label={activeLabel}>
      <div className={classes.header}>
        <span className={classes.headerText}>{activeLabel}</span>
      </div>
      <div className={classes.tabBar}>
        <Render.If condition={canScrollLeft}>
          <BackButton className={classes.scrollButton}
            aria-label='Scroll navigation left'
            onClick={handleScrollLeft} />
        </Render.If>
        <div className={classes.tabGroup}>
          <div className={classes.scrollArea}
            ref={scrollRef}
            onScroll={updateScrollState}>
            {secondaryMenuItems.map((item, index) => {
              const selected = isSelectedItem(item, currentPage);
              return (
                <button key={item.key ?? item.label}
                  className={classes.tab}
                  type='button'
                  data-index={index}
                  data-selected={selected || undefined}
                  onClick={handleTabClick}>
                  <div className={classnames(classes.tabPill, {
                    [classes.tabPillActive] : selected
                  })}>
                    <div className={classes.tabTextGroup}>
                      <span className={classnames(classes.tabText, {
                        [classes.tabTextActive] : selected
                      })}>
                        {item.label}
                      </span>
                      <span className={classnames(classes.tabText, classes.tabTextSizer)} aria-hidden>
                        {item.label}
                      </span>
                    </div>
                    <Render.If condition={!!item.meta?.tag?.label}>
                      <span className={classes.betaTag}>
                        {translate(item.meta?.tag?.label)}
                      </span>
                    </Render.If>
                  </div>
                  <div className={classnames(classes.tabIndicator, {
                    [classes.tabIndicatorActive] : selected
                  })} />
                </button>
              );
            })}
          </div>
        </div>
        <Render.If condition={canScrollRight}>
          <ForwardButton className={classes.scrollButton}
            aria-label='Scroll navigation right'
            onClick={handleScrollRight} />
        </Render.If>
      </div>
    </nav>
  );
};

export default memo(NavigationTopBar);
